/* PawsOn 2026 — reviews carousel.
 *
 * Scrolls the viewport rather than transforming a track, so native swipe,
 * keyboard scrolling and scroll-snap all keep working. Dot count is measured
 * from the real scroll range, so no dot is ever a dead position.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initCarousel(root) {
    if (root.dataset.pwCarouselReady) return;
    root.dataset.pwCarouselReady = 'true';

    var viewport = root.querySelector('[data-pw-viewport]');
    var track = root.querySelector('.pw-reviews__track');
    var controls = root.querySelector('[data-pw-controls]');
    var dotsWrap = root.querySelector('[data-pw-dots]');
    var prev = root.querySelector('[data-pw-prev]');
    var next = root.querySelector('[data-pw-next]');
    var cards = track ? track.children : [];
    if (!viewport || !track || !cards.length) return;

    var autoplayMs = parseInt(root.dataset.autoplay, 10) || 0;
    var timer = null;
    var positions = [0];
    var stops = 1;
    var index = 0;

    /* One card plus one gap — the distance a single advance travels. */
    function step() {
      var first = cards[0];
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    }

    /* Build the real scroll positions rather than counting stops and
       multiplying. Counting overshoots the end, and when the overshoot is
       small the final stop lands a few pixels from the previous one — the dot
       advances and the cards visibly don't move. Here the last stop is always
       the true scroll end, and it is merged into the previous one when there
       is nothing left to travel. */
    function computePositions() {
      var range = viewport.scrollWidth - viewport.clientWidth;
      var s = step();
      if (range <= 1 || s <= 0) return [0];

      /* Never more stops than there are cards. step() is a live measurement,
         and a measurement taken mid-layout — fonts still swapping, or a phone
         part-way through a pinch — can come back a fraction of its settled
         value. The loop would then mint a dot every few pixels and the row
         would fill with them. Six reviews can never need a seventh stop. */
      var maxStops = cards.length;
      var pos = [];
      for (var x = 0; x < range && pos.length < maxStops; x += s) pos.push(x);

      var last = pos[pos.length - 1];
      if (range - last > 4 && pos.length < maxStops) pos.push(range);
      else pos[pos.length - 1] = range;
      return pos;
    }

    function measure() {
      positions = computePositions();
      stops = positions.length;
      if (index > stops - 1) index = stops - 1;
      renderDots();
      controls.hidden = stops <= 1;
      if (stops <= 1) stop();
      /* Edge fades are static; they simply exist when the row can scroll. */
      root.dataset.scrollable = stops > 1 ? 'true' : 'false';
    }

    function renderDots() {
      if (dotsWrap.children.length === stops) return syncDots();
      dotsWrap.textContent = '';
      for (var i = 0; i < stops; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pw-reviews__dot';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1) + ' of ' + stops);
        dotsWrap.appendChild(dot);
      }
      syncDots();
    }

    function syncDots() {
      Array.prototype.forEach.call(dotsWrap.children, function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function goTo(i, smooth) {
      index = (i + stops) % stops;
      viewport.scrollTo({
        left: positions[index],
        behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto',
      });
      syncDots();
    }

    function start() {
      stop();
      if (!autoplayMs || stops <= 1 || reduceMotion.matches) return;
      timer = window.setInterval(function () {
        goTo(index + 1, true);
      }, autoplayMs);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    prev.addEventListener('click', function () {
      goTo(index - 1, true);
      start();
    });
    next.addEventListener('click', function () {
      goTo(index + 1, true);
      start();
    });

    dotsWrap.addEventListener('click', function (event) {
      var dot = event.target.closest('.pw-reviews__dot');
      if (!dot) return;
      goTo(parseInt(dot.dataset.index, 10), true);
      start();
    });

    /* Auto-advance is hostile if it moves while someone is reading or tabbing
       through, so pause on hover and on focus, and while the tab is hidden. */
    root.addEventListener('pointerenter', stop);
    root.addEventListener('pointerleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', function (event) {
      if (!root.contains(event.relatedTarget)) start();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    /* Keep the dots honest when the reader swipes the viewport directly. */
    var scrollTick;
    viewport.addEventListener('scroll', function () {
      window.clearTimeout(scrollTick);
      scrollTick = window.setTimeout(function () {
        /* Nearest configured position, so a manual swipe lands on the same
           dot the arrows would have selected. */
        var left = viewport.scrollLeft;
        var nearest = 0;
        for (var i = 1; i < positions.length; i++) {
          if (Math.abs(positions[i] - left) < Math.abs(positions[nearest] - left)) nearest = i;
        }
        if (nearest !== index) {
          index = nearest;
          syncDots();
        }
      }, 120);
    });

    var resizeTick;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTick);
      resizeTick = window.setTimeout(measure, 150);
    });

    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () {
        start();
      });
    }

    measure();
    start();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-pw-carousel]').forEach(initCarousel);
  }

  if (document.readyState !== 'loading') {
    init(document);
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
